import Tabs, { TabList, TabPanel } from '@atlaskit/tabs';
import { css } from '@compiled/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactElement } from 'react';

import { MyLearningOnDemandTabs, ON_DEMAND_TAB_PATHS } from 'src/pages/MyLearningPage/children/MyLearningOnDemandTabs';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';

import { CustomTab } from './CustomTab';
import { LiveTrainingTab } from './LiveTrainingTab';

export const MY_LEARNING_TAB_PATHS = ['on-demand', 'live-training', 'certifications'];

interface MyLearningTabsProps {
  activeTabIndex: number;
}

export const MyLearningTabs = ({ activeTabIndex }: MyLearningTabsProps): ReactElement => {
  const { user } = useAtlassianUser();

  const location = useLocation();
  const navigate = useNavigate();

  const handleTabChange = (index: number): void => {
    const targetPath = MY_LEARNING_TAB_PATHS[index];

    // For on-demand, navigate to the first sub-tab (in-progress)
    if (targetPath === MyLearningPageContentSections.ON_DEMAND) {
      navigate('/my-learning/on-demand/in-progress');
    } else {
      navigate(`/my-learning/${targetPath}`);
    }

    user &&
      sendUIEvent(
        'myLearningScreen',
        'tab',
        'clicked',
        'myLearningTab',
        {
          learningTab: targetPath,
        },
        user.account_id,
      );
  };

  return (
    <div css={tabsListContainerStyles} data-testid="my-learning-tabs">
      <Tabs onChange={handleTabChange} id="default" defaultSelected={activeTabIndex} selected={activeTabIndex}>
        <TabList>
          <CustomTab label="On-demand" />
          <CustomTab label="Live Training" />
          <CustomTab label="Certifications" />
        </TabList>
        {MY_LEARNING_TAB_PATHS.map((path, idx) => {
          // Calculate activeTabIndex for on-demand sub-tabs
          const onDemandActiveTabIndex = ON_DEMAND_TAB_PATHS.findIndex((subPath) =>
            location.pathname.includes(subPath),
          );

          const category = MY_LEARNING_TAB_PATHS[idx];

          return (
            <TabPanel key={path}>
              <div css={emptyTabContainerStyles} key={path}>
                {path === MyLearningPageContentSections.ON_DEMAND && (
                  <MyLearningOnDemandTabs category={category} activeTabIndex={Math.max(0, onDemandActiveTabIndex)} />
                )}
                {path === MyLearningPageContentSections.LIVE_TRAININGS && <LiveTrainingTab />}
                {path === MyLearningPageContentSections.CERTIFICATIONS && (
                  <div data-testid="certifications">Certifications</div>
                )}
              </div>
            </TabPanel>
          );
        })}
      </Tabs>
    </div>
  );
};

const tabsListContainerStyles = css({
  marginTop: '28px',
});

const emptyTabContainerStyles = css({
  width: '100%',
  margin: '0 auto',
});
